import React, { useState, useEffect } from 'react';
import { 
  FiChevronDown, 
  FiChevronUp, 
  FiShare2, 
  FiPercent,
  FiToggleLeft,
  FiToggleRight,
  FiInfo,
  FiAlertCircle
} from 'react-icons/fi';
import { useReferralApi } from '../../referal/hooks/useReferralApi';

/**
 * EventReferralSettings Component
 * Collapsible section for configuring event-specific referral settings
 * Integrated into Event Details (Step 1) of Create/Edit Event forms
 */
const EventReferralSettings = ({ 
  referralSettings, 
  onChange,
  isEditMode = false 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [globalPercentage, setGlobalPercentage] = useState(null);
  const [loadingGlobal, setLoadingGlobal] = useState(false);

  const { getPercentage } = useReferralApi();

  // Default settings structure
  const defaultSettings = {
    enableReferral: true,
    referralPercentageOverride: null, // null means use global
    useCustomPercentage: false,
  };

  // Merge with defaults
  const settings = {
    ...defaultSettings,
    ...referralSettings,
  };

  // Fetch global percentage on mount
  useEffect(() => {
    const fetchGlobalPercentage = async () => {
      setLoadingGlobal(true);
      try {
        const data = await getPercentage();
        setGlobalPercentage(data?.referral_percentage ?? 0);
      } catch (err) {
        console.error('Failed to fetch global percentage:', err);
        setGlobalPercentage(0);
      } finally {
        setLoadingGlobal(false);
      }
    };
    fetchGlobalPercentage();
  }, [getPercentage]);

  const handleToggleReferral = () => {
    onChange({
      ...settings,
      enableReferral: !settings.enableReferral,
    });
  };

  const handleToggleCustomPercentage = () => {
    const newUseCustom = !settings.useCustomPercentage;
    onChange({
      ...settings,
      useCustomPercentage: newUseCustom,
      referralPercentageOverride: newUseCustom ? (globalPercentage ?? 0) : null,
    });
  };

  const handlePercentageChange = (e) => {
    let value = parseFloat(e.target.value);
    if (isNaN(value)) value = 0;
    if (value < 0) value = 0;
    if (value > 100) value = 100;
    
    onChange({
      ...settings,
      referralPercentageOverride: value,
    });
  };

  const effectivePercentage = settings.useCustomPercentage && settings.referralPercentageOverride !== null
    ? settings.referralPercentageOverride
    : globalPercentage;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      {/* Collapsible Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-indigo-50 to-purple-50 hover:from-indigo-100 hover:to-purple-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="bg-indigo-500 text-white p-2 rounded-lg">
            <FiShare2 className="text-lg" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-800">Referral Settings</h3>
            <p className="text-sm text-gray-500">
              {settings.enableReferral 
                ? `Enabled • ${effectivePercentage ?? '...'}% reward rate`
                : 'Disabled for this event'
              }
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Quick status indicator */}
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            settings.enableReferral 
              ? 'bg-green-100 text-green-700' 
              : 'bg-gray-100 text-gray-600'
          }`}>
            {settings.enableReferral ? 'ON' : 'OFF'}
          </span>
          {isExpanded ? (
            <FiChevronUp className="text-gray-500 text-xl" />
          ) : (
            <FiChevronDown className="text-gray-500 text-xl" />
          )}
        </div>
      </button>

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="p-4 border-t border-gray-200 space-y-5">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              {settings.enableReferral ? (
                <FiToggleRight className="text-3xl text-green-500" />
              ) : (
                <FiToggleLeft className="text-3xl text-gray-400" />
              )}
              <div>
                <p className="font-medium text-gray-800">Enable Referral Tracking</p>
                <p className="text-sm text-gray-500">
                  Allow users to use referral codes when participating in this event
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleToggleReferral}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                settings.enableReferral
                  ? 'bg-green-500 text-white hover:bg-green-600'
                  : 'bg-gray-300 text-gray-600 hover:bg-gray-400'
              }`}
            >
              {settings.enableReferral ? 'Enabled' : 'Disabled'}
            </button>
          </div>

          {settings.enableReferral && (
            <>
              {/* Global vs Custom Percentage */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FiPercent className="text-indigo-500" />
                    <span className="font-medium text-gray-800">Referral Percentage</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Use custom:</span>
                    <button
                      type="button"
                      onClick={handleToggleCustomPercentage}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        settings.useCustomPercentage ? 'bg-indigo-500' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          settings.useCustomPercentage ? 'translate-x-7' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Global Percentage Display */}
                <div className={`p-3 rounded-lg border ${
                  settings.useCustomPercentage 
                    ? 'bg-gray-50 border-gray-200' 
                    : 'bg-indigo-50 border-indigo-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Global Percentage</p>
                      <p className={`text-2xl font-bold ${
                        settings.useCustomPercentage ? 'text-gray-400' : 'text-indigo-600'
                      }`}>
                        {loadingGlobal ? '...' : `${globalPercentage ?? 0}%`}
                      </p>
                    </div>
                    {!settings.useCustomPercentage && (
                      <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded">
                        Active
                      </span>
                    )}
                  </div>
                </div>

                {/* Custom Percentage Input */}
                {settings.useCustomPercentage && (
                  <div className="p-3 rounded-lg border border-purple-200 bg-purple-50">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-600">Custom Percentage for this Event</p>
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                        Active
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={settings.referralPercentageOverride ?? 0}
                        onChange={handlePercentageChange}
                        className="flex-1 px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg font-medium"
                      />
                      <span className="text-xl font-medium text-gray-500">%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="0.1"
                      value={settings.referralPercentageOverride ?? 0}
                      onChange={handlePercentageChange}
                      className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer accent-purple-500 mt-3"
                    />
                  </div>
                )}
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <FiInfo className="text-blue-500 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">How event referrals work:</p>
                    <ul className="list-disc list-inside space-y-1 text-blue-700">
                      <li>Users enter a referral code when joining this event</li>
                      <li>Referrer (Level 1) gets <strong>{effectivePercentage ?? 0}%</strong> of the entry fee</li>
                      <li>Referrer's referrer (Level 2) also gets <strong>{effectivePercentage ?? 0}%</strong></li>
                      <li>Rewards are credited after successful participation</li>
                    </ul>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Disabled State Info */}
          {!settings.enableReferral && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <FiAlertCircle className="text-yellow-500 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium">Referral tracking disabled</p>
                  <p className="text-yellow-700 mt-1">
                    Users will not be able to use or enter referral codes when participating in this event. 
                    No referral rewards will be distributed for this event.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EventReferralSettings;
